"use client"

import React from "react"
import { Check, ChevronDown, Info, X, FileText, Download } from "lucide-react"
import * as SelectPrimitive from "@radix-ui/react-select"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { DATASETS } from "../data/datasets"
import RunConfigWizard from "./RunConfigWizard"
import { PG_BASELINE_RUNS, PG_EVALUATION_RUNS } from "../data/pg_runs_all"
import EvaluationCharts from "./EvaluationCharts"

const datasetById = Object.fromEntries(DATASETS.map((d) => [d.id, d]))

const dtf = new Intl.DateTimeFormat(undefined, {
  dateStyle: "medium",
  timeStyle: "short",
})
const rtf = new Intl.RelativeTimeFormat(undefined, { numeric: "auto" })

function timeAgo(ts) {
  if (!ts) return null
  const diffSec = Math.round((ts - Date.now()) / 1000)
  const abs = Math.abs(diffSec)
  const units = [
    ["year", 31536000],
    ["month", 2592000],
    ["week", 604800],
    ["day", 86400],
    ["hour", 3600],
    ["minute", 60],
    ["second", 1],
  ]
  for (const [unit, size] of units) {
    if (abs >= size || unit === "second") {
      return rtf.format(Math.round(diffSec / size), unit)
    }
  }
  return null
}

const Select = SelectPrimitive.Root

const SelectTrigger = React.forwardRef(
  ({ className, children, ...props }, ref) => (
    <SelectPrimitive.Trigger
      ref={ref}
      className={[
        "flex h-9 w-full items-center justify-between rounded-md border px-3 text-sm",
        "border-gray-300 bg-white text-gray-900 placeholder:text-gray-500",
        "dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100 dark:placeholder:text-slate-400",
        "focus:outline-none focus:ring-2 focus:ring-blue-500/60",
        "disabled:cursor-not-allowed disabled:opacity-50",
        className || "",
      ].join(" ")}
      {...props}
    >
      {children}
      <SelectPrimitive.Icon asChild>
        <ChevronDown className="h-4 w-4 opacity-60" />
      </SelectPrimitive.Icon>
    </SelectPrimitive.Trigger>
  )
)
SelectTrigger.displayName = "SelectTrigger"

const SelectContent = React.forwardRef(
  ({ className, children, position = "popper", ...props }, ref) => (
    <SelectPrimitive.Portal>
      <SelectPrimitive.Content
        ref={ref}
        position={position}
        className={[
          "relative z-50 min-w-[10rem] overflow-hidden rounded-md border shadow-xl",
          "bg-white border-gray-200 text-gray-900",
          "dark:bg-slate-900 dark:text-slate-100 dark:border-slate-700",
          "ring-1 ring-black/5 dark:ring-white/10",
          "data-[state=open]:animate-in data-[state=closed]:animate-out",
          position === "popper" &&
            "data-[side=bottom]:translate-y-1 data-[side=top]:-translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1",
          className || "",
        ].join(" ")}
        {...props}
      >
        <SelectPrimitive.ScrollUpButton className="flex items-center justify-center py-1 text-gray-500 dark:text-slate-400">
          <ChevronDown className="h-4 w-4 rotate-180" />
        </SelectPrimitive.ScrollUpButton>

        <SelectPrimitive.Viewport className="p-1">
          {children}
        </SelectPrimitive.Viewport>

        <SelectPrimitive.ScrollDownButton className="flex items-center justify-center py-1 text-gray-500 dark:text-slate-400">
          <ChevronDown className="h-4 w-4" />
        </SelectPrimitive.ScrollDownButton>
      </SelectPrimitive.Content>
    </SelectPrimitive.Portal>
  )
)
SelectContent.displayName = "SelectContent"

const SelectItem = React.forwardRef(
  ({ className, children, ...props }, ref) => (
    <SelectPrimitive.Item
      ref={ref}
      className={[
        "relative flex w-full cursor-default select-none items-center rounded-sm py-2 pl-8 pr-3 text-sm outline-none",
        "text-gray-900 dark:text-slate-100",
        "hover:bg-gray-100 data-[highlighted]:bg-gray-100",
        "dark:hover:bg-slate-800 dark:data-[highlighted]:bg-slate-800",
        "data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
        className || "",
      ].join(" ")}
      {...props}
    >
      <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
        <SelectPrimitive.ItemIndicator>
          <Check className="h-4 w-4 opacity-80" />
        </SelectPrimitive.ItemIndicator>
      </span>
      <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
    </SelectPrimitive.Item>
  )
)
SelectItem.displayName = "SelectItem"

const Dialog = DialogPrimitive.Root
const DialogTrigger = DialogPrimitive.Trigger

const DialogContent = React.forwardRef(
  ({ className, children, ...props }, ref) => (
    <DialogPrimitive.Portal>
      <DialogPrimitive.Overlay
        className={[
          "fixed inset-0 z-50 backdrop-blur-sm",
          "bg-black/40 dark:bg-black/60",
        ].join(" ")}
      />
      <DialogPrimitive.Content
        ref={ref}
        className={[
          "fixed left-1/2 top-1/2 z-50 grid w-full max-w-lg -translate-x-1/2 -translate-y-1/2 gap-4",
          "rounded-lg border p-6 shadow-2xl",
          "bg-white text-gray-900 border-gray-200",
          "dark:bg-slate-900 dark:text-slate-100 dark:border-slate-700 ring-1 ring-black/5 dark:ring-white/10",
          "max-h-[85vh] overflow-y-auto",
          className || "",
        ].join(" ")}
        {...props}
      >
        {children}
        <DialogPrimitive.Close className="absolute right-4 top-4 rounded-sm opacity-70 hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-blue-500/60">
          <X className="h-4 w-4" />
          <span className="sr-only">Close</span>
        </DialogPrimitive.Close>
      </DialogPrimitive.Content>
    </DialogPrimitive.Portal>
  )
)
DialogContent.displayName = "DialogContent"

const DialogHeader = ({ className, ...props }) => (
  <div
    className={`flex flex-col space-y-1.5 text-center sm:text-left ${
      className || ""
    }`}
    {...props}
  />
)

const DialogTitle = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={[
      "text-lg font-semibold leading-none tracking-tight",
      "text-gray-900 dark:text-slate-100",
    ].join(" ")}
    {...props}
  />
))
DialogTitle.displayName = "DialogTitle"

function triggerDownload(name, data, mime = "text/plain") {
  try {
    const blob = new Blob([data], { type: mime })
    const url = URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = url
    a.download = name
    a.click()
    URL.revokeObjectURL(url)
  } catch {}
}

function downloadArtifact(artifact, fallbackName, fallbackMime) {
  if (!artifact) return
  if (typeof artifact === "string") {
    if (/^https?:\/\//i.test(artifact)) {
      window.open(artifact, "_blank")
    } else {
      triggerDownload(fallbackName, artifact, fallbackMime)
    }
    return
  }
  if (artifact.url) {
    window.open(artifact.url, "_blank")
    return
  }
  if (artifact.content) {
    triggerDownload(
      artifact.name || fallbackName,
      artifact.content,
      artifact.mime || fallbackMime
    )
  }
}

// Color classes for comparison deltas vs baseline
function deltaClasses(delta) {
  const base = "text-right font-mono px-1 rounded"

  if (delta >= 0) {
    return [
      base,
      "bg-emerald-50 text-emerald-700",
      "dark:bg-emerald-900/40 dark:text-emerald-300",
    ].join(" ")
  }

  if (delta > -0.05) {
    return [
      base,
      "bg-amber-50 text-amber-700",
      "dark:bg-amber-900/40 dark:text-amber-300",
    ].join(" ")
  }

  return [
    base,
    "bg-red-50 text-red-700",
    "dark:bg-red-900/40 dark:text-red-300",
  ].join(" ")
}

function propertyMetaParts(prop, { strict = false } = {}) {
  const parts = []

  if (prop.type) parts.push(prop.type)
  if (strict && typeof prop.optional === "boolean") {
    parts.push(prop.optional ? "optional" : "required")
  }
  if (strict && prop.cardinality) {
    parts.push(prop.cardinality)
  }
  if (strict && (prop.min != null || prop.max != null)) {
    parts.push(`${prop.min ?? 0}..${prop.max != null ? prop.max : "N"}`)
  }

  return parts
}

function renderPropertyChips(details, { strict = false, emptyLabel = "No properties" } = {}) {
  if (!details?.length) {
    return (
      <p className="text-sm italic text-gray-400 dark:text-slate-500">
        {emptyLabel}
      </p>
    )
  }

  return (
    <div className="flex flex-wrap gap-2">
      {details.map((prop, index) => {
        const metaParts = propertyMetaParts(prop, { strict })

        return (
          <div
            key={`${prop.name || "prop"}-${index}`}
            className="inline-flex max-w-full items-center gap-2 rounded-full border border-gray-200 bg-gray-50/80 px-3 py-1.5 dark:border-slate-700 dark:bg-slate-900/70"
          >
            <div className="break-all text-sm font-medium text-gray-900 dark:text-slate-100">
              {prop.name}
            </div>
            {metaParts.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {metaParts.map((part, partIndex) => (
                  <span
                    key={`${prop.name || "prop"}-${index}-meta-${partIndex}`}
                    className="inline-flex items-center rounded-full bg-white px-2 py-0.5 text-[10px] font-medium text-slate-600 ring-1 ring-gray-200 dark:bg-slate-800 dark:text-slate-300 dark:ring-slate-700"
                  >
                    {part}
                  </span>
                ))}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

function formatAlgorithmLabel(variant) {
  switch (variant) {
    case "ELSH":
      return "ELSH"
    case "MinHash":
      return "MinHash LSH"
    case "QUANTUM_FAST":
      return "QUANTUM (fast)"
    case "QUANTUM":
      return "QUANTUM"
    default:
      return variant || "Unknown"
  }
}

export default function GraphControlsPanel({
  datasetId,
  datasetLabel,
  runParams,
  setRunParams,
  nodeTypes,
  edges,
  metrics,
  colorFor,
  onDatasetChange,
  onRunClick,
  onShowLogs,
  dialogs,
  lastRunTs,
  artifacts,
  activeRun,          // νέο prop: τρέχον run
  onShowIterations,   // νέο prop: άνοιγμα Iterations modal
}) {
  const { nodeTypesOpen, setNodeTypesOpen, edgesOpen, setEdgesOpen } = dialogs

  const lastAbs = lastRunTs ? dtf.format(new Date(lastRunTs)) : "—"
  const lastRel = lastRunTs ? timeAgo(lastRunTs) : null

  const [runModalOpen, setRunModalOpen] = React.useState(false)

  // Baselines state
  const [baselineModalOpen, setBaselineModalOpen] = React.useState(false)
  const [baselineChoice, setBaselineChoice] = React.useState("gmm") // "gmm" | "shemI"
  const [isBaselineRunning, setIsBaselineRunning] = React.useState(false)
  const [baselineResults, setBaselineResults] = React.useState({
    gmm: null,
    shemI: null,
  })
  const [selectedBaseline, setSelectedBaseline] = React.useState(null) // "gmm" | "shemI" | null

  // Evaluation state
  const [isEvaluationRunning, setIsEvaluationRunning] = React.useState(false)
  const [evaluationResults, setEvaluationResults] = React.useState(null)
  const [showEvaluationModal, setShowEvaluationModal] = React.useState(false)
  const [edgeSortBy, setEdgeSortBy] = React.useState("label")

  const baselineLabel = (id) => (id === "gmm" ? "GMM" : "ShemI")

  // Auto-load evaluation results when extensiveEvaluation is enabled
  React.useEffect(() => {
    if (runParams?.extensiveEvaluation && datasetId) {
      const datasetEvaluations = PG_EVALUATION_RUNS[datasetId] || {}
      const evaluationRun = datasetEvaluations["detailed"] || null

      if (evaluationRun) {
        setEvaluationResults(evaluationRun)
      } else {
        setEvaluationResults(null)
      }
    } else {
      setEvaluationResults(null)
    }
  }, [runParams?.extensiveEvaluation, datasetId])

  // Manual run evaluation (when checkbox is not checked)
  function handleRunEvaluation() {
    if (isEvaluationRunning) return
    setIsEvaluationRunning(true)

    setTimeout(() => {
      const datasetEvaluations = PG_EVALUATION_RUNS[datasetId] || {}
      const evaluationRun = datasetEvaluations["detailed"] || null

      if (evaluationRun) {
        setEvaluationResults(evaluationRun)
      } else {
        setEvaluationResults(null)
      }

      setIsEvaluationRunning(false)
    }, 1200)
  }

  const STORAGE_KEY = `pg_baselines_${datasetId}`

  // Restore baselines μόνο από sessionStorage.
  // ΔΕΝ κάνουμε fallback στα PG_BASELINE_RUNS για να μην φαίνονται πριν τρέξεις baseline.
  React.useEffect(() => {
    if (typeof window === "undefined") return

    // try {
    //   const raw = window.sessionStorage.getItem(STORAGE_KEY)
    //   if (raw) {
    //     const parsed = JSON.parse(raw)
    //     setBaselineResults({
    //       gmm: parsed.gmm || null,
    //       shemI: parsed.shemI || null,
    //     })
    //     setSelectedBaseline(parsed.selectedBaseline || null)
    //   } else {
    //     // καθαρή πρώτη φορά
    //     setBaselineResults({ gmm: null, shemI: null })
    //     setSelectedBaseline(null)
    //   }
    // } catch {
    //   setBaselineResults({ gmm: null, shemI: null })
    //   setSelectedBaseline(null)
    // }
  }, [STORAGE_KEY])

  function persistBaselines(nextResults, nextSelected) {
    if (typeof window === "undefined") return
    try {
      const payload = {
        gmm: nextResults.gmm || null,
        shemI: nextResults.shemI || null,
        selectedBaseline: nextSelected || null,
      }
      window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(payload))
    } catch {
      // ignore
    }
  }

  function handleRunBaseline() {
    if (!baselineChoice || isBaselineRunning) return
    setIsBaselineRunning(true)

    setTimeout(() => {
      const datasetBaselines = PG_BASELINE_RUNS[datasetId] || {}
      let baselineRun = datasetBaselines[baselineChoice] || null

      if (!baselineRun) {
        // ΔΕΝ υπάρχει πραγματικό baseline → φτιάξε demo ΜΟΝΟ για το συγκεκριμένο view (δεν το κάνουμε persist)
        const baseNodes = metrics?.nodes || {
          f1Score: 0.7,
          precision: 0.7,
          recall: 0.7,
        }
        const baseEdges = metrics?.edges || {
          f1Score: 0.65,
          precision: 0.65,
          recall: 0.65,
        }

        const fudge = baselineChoice === "gmm" ? -0.05 : -0.1
        const clamp01 = (v) =>
          Math.max(0, Math.min(1, Number.isFinite(v) ? v : 0))

        const makeShifted = (obj, df) => ({
          f1Score: clamp01((obj.f1Score || 0) + df),
          precision: clamp01((obj.precision || 0) + df),
          recall: clamp01((obj.recall || 0) + df),
        })

        const nodesDummy = makeShifted(baseNodes, fudge)
        const edgesDummy = makeShifted(baseEdges, fudge)

        baselineRun = {
          id: `baseline_${baselineChoice}_${datasetId}`,
          ts: Date.now(),
          metrics:
            baselineChoice === "gmm"
              ? { nodes: nodesDummy } // node-only baseline
              : { nodes: nodesDummy, edges: edgesDummy },
          params: { kind: baselineChoice, dataset: datasetId },
        }

        const nextResults = {
          ...baselineResults,
          [baselineChoice]: baselineRun,
        }
        setBaselineResults(nextResults)
        setSelectedBaseline(baselineChoice)
        setIsBaselineRunning(false)
        setBaselineModalOpen(false)
        // demo baselines ΔΕΝ γίνονται persist
        return
      }

      // Υπάρχει πραγματικό baseline → αποθήκευσέ το και κάνε persist
      const nextResults = {
        ...baselineResults,
        [baselineChoice]: baselineRun,
      }

      setBaselineResults(nextResults)
      setSelectedBaseline(baselineChoice)
      setIsBaselineRunning(false)
      setBaselineModalOpen(false)
      persistBaselines(nextResults, baselineChoice)
    }, 800)
  }

  const selectedBaselineRun = selectedBaseline
    ? baselineResults[selectedBaseline]
    : null
  const baselineMetrics = selectedBaselineRun?.metrics
  const displayedParams = activeRun?.params || runParams || {}
  const displayedVariant = displayedParams.lshVariant || "ELSH"
  const displayedIsQuantum =
    displayedVariant === "QUANTUM_FAST" || displayedVariant === "QUANTUM"
  const displayedIsLsh =
    displayedVariant === "ELSH" || displayedVariant === "MinHash"
  const pushConfigItem = (label, value) => {
    if (value == null || value === "") return
    runConfigItems.push({ label, value: String(value) })
  }
  const runConfigItems = [
    {
      label: "Algorithm",
      value: formatAlgorithmLabel(displayedVariant),
    },
    {
      label: "Mode",
      value: displayedIsQuantum
        ? displayedParams.adaptive
          ? "Auto-K"
          : "Fixed K"
        : displayedParams.adaptive
          ? "Auto-tuned"
          : "Custom settings",
    },
    {
      label: "Incremental",
      value: displayedParams.incremental
        ? displayedParams.incrementalValue
          ? `Yes (${Number(displayedParams.incrementalValue).toLocaleString()})`
          : "Yes"
        : "No",
    },
  ]

  if (displayedIsQuantum) {
    runConfigItems.push({
      label: "Balancing",
      value: displayedParams.spectralAutoBalancing ? "Spectral auto" : "Manual",
    })
    pushConfigItem(
      "Balance target",
      displayedParams.quantumBalanceTarget
    )
    pushConfigItem(
      "Balance lambda",
      displayedParams.quantumBalanceLambda
    )
    pushConfigItem("Power iters", displayedParams.quantumPowerIters)
    pushConfigItem("Tolerance", displayedParams.quantumTolerance)
    pushConfigItem("SA iters", displayedParams.quantumSaIters)
    pushConfigItem("SA restarts", displayedParams.quantumSaRestarts)
    pushConfigItem(
      "Min similarity",
      displayedParams.quantumMinSimilarity
    )
    pushConfigItem(
      "Min cluster",
      displayedParams.quantumMinClusterSize
    )
    pushConfigItem("Fixed K", displayedParams.quantumFixedK)
    pushConfigItem("Top K", displayedParams.quantumTopK)
    pushConfigItem(
      "Max group",
      displayedParams.quantumMaxGroupSize
    )
    pushConfigItem("Random seed", displayedParams.quantumRandomSeed)
  }

  if (!displayedParams.adaptive && displayedIsLsh) {
    if (displayedParams.lshHashTables != null) {
      runConfigItems.push({
        label: "Hash tables",
        value: String(displayedParams.lshHashTables),
      })
    }
    if (
      displayedVariant === "ELSH" &&
      displayedParams.lshHashBucketLength != null
    ) {
      runConfigItems.push({
        label: "Bucket length",
        value: String(displayedParams.lshHashBucketLength),
      })
    }
    if (displayedParams.jaccardSimilarityThreshold != null) {
      runConfigItems.push({
        label: "Jaccard",
        value: String(displayedParams.jaccardSimilarityThreshold),
      })
    }
  }

  if (
    displayedParams.knownTypeLabelPercentage != null ||
    displayedParams.knownNodesPercentage != null
  ) {
    runConfigItems.push({
      label: "Known labels",
      value: `${displayedParams.knownTypeLabelPercentage ?? displayedParams.knownNodesPercentage}%`,
    })
  }

  if (displayedParams.extensiveEvaluation != null) {
    runConfigItems.push({
      label: "Extensive eval",
      value: displayedParams.extensiveEvaluation ? "On" : "Off",
    })
  }
  const totalNodeLooseProps = nodeTypes.reduce(
    (sum, type) => sum + (type.looseDetails?.length || 0),
    0
  )
  const totalNodeStrictProps = nodeTypes.reduce(
    (sum, type) => sum + (type.strictDetails?.length || 0),
    0
  )
  const totalEdgeLooseProps = edges.reduce(
    (sum, edge) => sum + (edge.looseDetails?.length || 0),
    0
  )
  const totalEdgeStrictProps = edges.reduce(
    (sum, edge) => sum + (edge.strictDetails?.length || 0),
    0
  )
  const sortedEdges = React.useMemo(() => {
    const copy = [...edges]
    const collator = new Intl.Collator(undefined, {
      numeric: true,
      sensitivity: "base",
    })

    copy.sort((a, b) => {
      const labelA = String(a.relationship || "")
      const labelB = String(b.relationship || "")
      const sourceA = String(a.source || "")
      const sourceB = String(b.source || "")
      const targetA = String(a.target || "")
      const targetB = String(b.target || "")

      if (edgeSortBy === "source") {
        return (
          collator.compare(sourceA, sourceB) ||
          collator.compare(labelA, labelB) ||
          collator.compare(targetA, targetB)
        )
      }

      if (edgeSortBy === "target") {
        return (
          collator.compare(targetA, targetB) ||
          collator.compare(labelA, labelB) ||
          collator.compare(sourceA, sourceB)
        )
      }

      return (
        collator.compare(labelA, labelB) ||
        collator.compare(sourceA, sourceB) ||
        collator.compare(targetA, targetB)
      )
    })

    return copy
  }, [edges, edgeSortBy])

  return (
    <div className="w-2/5 min-w-[380px] max-w-[560px] h-full p-3 flex flex-col border-r border-gray-200 dark:border-gray-800 overflow-y-auto bg-white dark:bg-gray-900">
      <div className="space-y-3">
        {/* Dataset header + downloads */}
        <div className="rounded-lg p-2 shadow-sm border bg-white/90 backdrop-blur supports-[backdrop-filter]:bg-white/90 border-gray-200 dark:bg-slate-800/90 dark:border-slate-700">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-slate-100">
                {datasetLabel}
              </h2>
              <p className="text-sm text-gray-600 dark:text-slate-300">
                {datasetById[datasetId]
                  ? `${datasetById[datasetId].nodes.toLocaleString()} nodes, ${datasetById[datasetId].edges.toLocaleString()} edges`
                  : null}
              </p>
              <p className="mt-1 text-xs text-gray-500 dark:text-slate-400">
                Date:{" "}
                <span className="font-medium text-gray-700 dark:text-slate-200">
                  {lastAbs}
                </span>
                {lastRel && (
                  <span className="ml-1 text-[11px] text-gray-400 dark:text-slate-500">
                    ({lastRel})
                  </span>
                )}
              </p>
            </div>
            <div className="z-10">
              <div className="relative group">
                <button
                  className="p-2 rounded-md bg-white hover:bg-gray-100 dark:bg-gray-800 dark:hover:bg-gray-700 transition-colors shadow-sm"
                  title="Download schema artifacts"
                >
                  <Download className="h-5 w-5 text-gray-700 dark:text-gray-200" />
                </button>

                <div
                  className="absolute right-0 mt-1 min-w-44 bg-white dark:bg-gray-800 rounded-md shadow-lg overflow-hidden z-20
                             opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all border border-gray-200 dark:border-gray-700"
                >
                  <div className="py-1">
                    <button
                      className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700 whitespace-nowrap
                                 ${
                                   artifacts?.xsd
                                     ? "text-gray-800 dark:text-gray-100"
                                     : "text-gray-400 dark:text-gray-500 cursor-not-allowed"
                                 }`}
                      disabled={!artifacts?.xsd}
                      onClick={() =>
                        downloadArtifact(
                          artifacts?.xsd,
                          "pg_schema.xsd",
                          "application/xml"
                        )
                      }
                    >
                      pg_schema.xsd
                    </button>

                    <button
                      className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700 whitespace-nowrap
                                 ${
                                   artifacts?.pgschema_loose
                                     ? "text-gray-800 dark:text-gray-100"
                                     : "text-gray-400 dark:text-gray-500 cursor-not-allowed"
                                 }`}
                      disabled={!artifacts?.pgschema_loose}
                      onClick={() =>
                        downloadArtifact(
                          artifacts?.pgschema_loose,
                          "pg_schema_loose.txt",
                          "text/plain"
                        )
                      }
                    >
                      pg_schema(Loose).txt
                    </button>

                    <button
                      className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700 whitespace-nowrap
                                 ${
                                   artifacts?.pgschema_strict
                                     ? "text-gray-800 dark:text-gray-100"
                                     : "text-gray-400 dark:text-gray-500 cursor-not-allowed"
                                 }`}
                      disabled={!artifacts?.pgschema_strict}
                      onClick={() =>
                        downloadArtifact(
                          artifacts?.pgschema_strict,
                          "pg_schema_strict.txt",
                          "text/plain"
                        )
                      }
                    >
                      pg_schema(Strict).txt
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="rounded-lg p-2 shadow-sm border bg-white/90 border-gray-200 dark:bg-slate-800/90 dark:border-slate-700">
          <div>
            <h3 className="text-sm font-semibold text-gray-900 dark:text-slate-100">
              Run Configuration
            </h3>
          </div>

          <div className="mt-2 flex flex-wrap gap-1.5">
            {runConfigItems.map((item) => (
              <div
                key={item.label}
                className="inline-flex max-w-full items-center gap-1.5 rounded-full border border-gray-200 bg-gray-50/80 px-2.5 py-1 dark:border-slate-700 dark:bg-slate-900/70"
              >
                <div className="text-[10px] font-medium uppercase tracking-wide text-gray-500 dark:text-slate-400">
                  {item.label}
                </div>
                <div className="text-[11px] font-medium text-gray-900 dark:text-slate-100 break-words">
                  {item.value}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Node types / Edges */}
        <div className="grid grid-cols-2 gap-3">
          {/* Node types */}
          <div className="rounded-lg p-2 shadow-sm border bg-white/90 border-gray-200 dark:bg-slate-800/90 dark:border-slate-700">
            <div className="flex items-center">
              <h3 className="text-base font-semibold text-gray-900 dark:text-slate-100">
                Node Types ({nodeTypes.length})
              </h3>
              <Dialog open={nodeTypesOpen} onOpenChange={setNodeTypesOpen}>
                <DialogTrigger asChild>
                  <button
                    className="ml-2 p-1 rounded-full bg-gray-200 hover:bg-gray-300 text-gray-800 transition-colors dark:bg-slate-700 dark:text-slate-100 dark:hover:bg-slate-600"
                    aria-label="Show node types"
                  >
                    <Info className="w-4 h-4" />
                  </button>
                </DialogTrigger>
                <DialogContent className="w-[min(1380px,98vw)] max-w-none max-h-[92vh]">
                  <DialogHeader>
                    <DialogTitle>Node Types</DialogTitle>
                    <p className="text-sm text-gray-500 dark:text-slate-400">
                      {nodeTypes.length} types, {totalNodeLooseProps} loose properties, {totalNodeStrictProps} strict properties
                    </p>
                  </DialogHeader>

                  <div className="max-h-[72vh] overflow-y-auto pr-2 space-y-3">
                    <div className="grid grid-cols-[minmax(220px,2.6fr)_minmax(0,3fr)_minmax(0,3fr)] gap-4 rounded-lg border border-gray-200 bg-slate-50 px-4 py-3 text-xs font-semibold uppercase tracking-wide text-slate-600 sticky top-0 dark:border-slate-700 dark:bg-slate-900/95 dark:text-slate-300">
                      <div>Type</div>
                      <div>Loose Properties</div>
                      <div>Strict Constraints</div>
                    </div>

                    {nodeTypes.map((type, index) => (
                      <div
                        key={`node-row-${index}`}
                        className="grid grid-cols-[minmax(220px,2.6fr)_minmax(0,3fr)_minmax(0,3fr)] gap-4 rounded-xl border border-gray-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-900"
                      >
                        <div>
                          <div className="flex items-start gap-3">
                            <span
                              className="mt-1 h-3.5 w-3.5 shrink-0 rounded-full"
                              style={{
                                backgroundColor: colorFor({ group: type.name }),
                              }}
                            />
                            <div className="min-w-0">
                              <p
                                className="text-base font-medium text-gray-900 dark:text-slate-100"
                                style={{ overflowWrap: "anywhere" }}
                              >
                                {type.name}
                              </p>
                              <div className="mt-2 flex flex-wrap gap-2">
                                <span className="inline-flex items-center rounded-full border border-gray-200 bg-gray-50 px-2 py-0.5 text-[11px] font-medium text-gray-700 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300">
                                  {type.looseDetails?.length || 0} loose
                                </span>
                                <span className="inline-flex items-center rounded-full border border-gray-200 bg-gray-50 px-2 py-0.5 text-[11px] font-medium text-gray-700 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300">
                                  {type.strictDetails?.length || 0} strict
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div>
                          {renderPropertyChips(type.looseDetails, {
                            emptyLabel: "No loose properties",
                          })}
                        </div>

                        <div>
                          {renderPropertyChips(type.strictDetails, {
                            strict: true,
                            emptyLabel: "No strict constraints",
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                </DialogContent>

              </Dialog>
            </div>
          </div>

          {/* Edges */}
          <div className="rounded-lg p-2 shadow-sm border bg-white/90 border-gray-200 dark:bg-slate-800/90 dark:border-slate-700">
            <div className="flex items-center">
              <h3 className="text-base font-semibold text-gray-900 dark:text-slate-100">
                Edge Types ({edges.length})
              </h3>
              <Dialog open={edgesOpen} onOpenChange={setEdgesOpen}>
                <DialogTrigger asChild>
                  <button
                    className="mt-1 ml-2 p-1 rounded-full bg-gray-200 hover:bg-gray-300 text-gray-800 transition-colors dark:bg-slate-700 dark:text-slate-100 dark:hover:bg-slate-600"
                    aria-label="Show edges"
                  >
                    <Info className="w-4 h-4" />
                  </button>
                </DialogTrigger>
                <DialogContent className="w-[min(1480px,98vw)] max-w-none max-h-[92vh]">
                  <DialogHeader>
                    <DialogTitle>Edge Types</DialogTitle>
                    <p className="text-sm text-gray-500 dark:text-slate-400">
                      {edges.length} edge types, {totalEdgeLooseProps} loose properties, {totalEdgeStrictProps} strict properties
                    </p>
                  </DialogHeader>

                  <div className="mt-3 flex items-center gap-3">
                    <span className="text-sm font-medium text-gray-700 dark:text-slate-300">
                      Sort by
                    </span>
                    <div className="w-44">
                      <Select value={edgeSortBy} onValueChange={setEdgeSortBy}>
                        <SelectTrigger className="h-9">
                          <SelectPrimitive.Value placeholder="Select sort" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="label">Edge label</SelectItem>
                          <SelectItem value="source">Source</SelectItem>
                          <SelectItem value="target">Target</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="max-h-[72vh] overflow-y-auto pr-2 space-y-3">
                    <div className="grid grid-cols-[minmax(180px,2.2fr)_minmax(160px,1.7fr)_minmax(180px,2.2fr)_minmax(180px,2fr)_minmax(220px,3fr)] gap-4 rounded-lg border border-gray-200 bg-slate-50 px-4 py-3 text-xs font-semibold uppercase tracking-wide text-slate-600 sticky top-0 dark:border-slate-700 dark:bg-slate-900/95 dark:text-slate-300">
                      <div>Source</div>
                      <div>Type</div>
                      <div>Target</div>
                      <div>Loose Properties</div>
                      <div>Strict Constraints</div>
                    </div>

                    {sortedEdges.map((edge, index) => (
                      <div
                        key={`edge-row-${index}`}
                        className="grid grid-cols-[minmax(180px,2.2fr)_minmax(160px,1.7fr)_minmax(180px,2.2fr)_minmax(180px,2fr)_minmax(220px,3fr)] gap-4 rounded-xl border border-gray-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-900"
                      >
                        <div
                          className="min-w-0 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm font-medium text-gray-800 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
                          style={{ overflowWrap: "anywhere" }}
                        >
                          {edge.source}
                        </div>

                        <div
                          className="min-w-0 rounded-lg bg-blue-50 px-3 py-2 text-sm font-semibold text-blue-700 dark:bg-blue-950/40 dark:text-blue-300"
                          style={{ overflowWrap: "anywhere" }}
                        >
                          {edge.relationship}
                        </div>

                        <div
                          className="min-w-0 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm font-medium text-gray-800 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
                          style={{ overflowWrap: "anywhere" }}
                        >
                          {edge.target}
                        </div>

                        <div className="min-w-0">
                          {renderPropertyChips(edge.looseDetails, {
                            emptyLabel: "No loose properties",
                          })}
                        </div>

                        <div className="min-w-0">
                          {renderPropertyChips(edge.strictDetails, {
                            strict: true,
                            emptyLabel: "No strict constraints",
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          </div>
        </div>

        {/* Metrics + baselines indicator */}
        <div className="rounded-lg p-2 shadow-sm border bg-white/90 border-gray-200 dark:bg-slate-800/90 dark:border-slate-700">
          <h3 className="text-base font-semibold mb-2 text-gray-900 dark:text-slate-100">
            Metrics
          </h3>
          <div className="grid grid-cols-2 gap-3">
            <div className="border rounded-md p-2 bg-gray-50 border-gray-200 dark:bg-slate-900 dark:border-slate-700">
              <div className="font-bold border-b border-gray-200 dark:border-slate-700 pb-1 mb-1 text-sm text-gray-900 dark:text-slate-100">
                Nodes
              </div>
              <div className="grid grid-cols-2 gap-1 text-xs">
                <div className="font-medium text-gray-700 dark:text-slate-300">
                  F1-score:
                </div>
                <div className="text-right font-mono text-emerald-700 dark:text-emerald-400">
                  {metrics.nodes.f1Score}
                </div>
                <div className="font-medium text-gray-700 dark:text-slate-300">
                  Precision:
                </div>
                <div className="text-right font-mono text-emerald-700 dark:text-emerald-400">
                  {metrics.nodes.precision}
                </div>
                <div className="font-medium text-gray-700 dark:text-slate-300">
                  Recall:
                </div>
                <div className="text-right font-mono text-emerald-700 dark:text-emerald-400">
                  {metrics.nodes.recall}
                </div>
              </div>
            </div>

            <div className="border rounded-md p-2 bg-gray-50 border-gray-200 dark:bg-slate-900 dark:border-slate-700">
              <div className="font-bold border-b border-gray-200 dark:border-slate-700 pb-1 mb-1 text-sm text-gray-900 dark:text-slate-100">
                Edges
              </div>
              <div className="grid grid-cols-2 gap-1 text-xs">
                <div className="font-medium text-gray-700 dark:text-slate-300">
                  F1-score:
                </div>
                <div className="text-right font-mono text-indigo-700 dark:text-indigo-400">
                  {metrics.edges.f1Score}
                </div>
                <div className="font-medium text-gray-700 dark:text-slate-300">
                  Precision:
                </div>
                <div className="text-right font-mono text-indigo-700 dark:text-indigo-400">
                  {metrics.edges.precision}
                </div>
                <div className="font-medium text-gray-700 dark:text-slate-300">
                  Recall:
                </div>
                <div className="text-right font-mono text-indigo-700 dark:text-indigo-400">
                  {metrics.edges.recall}
                </div>
              </div>
            </div>
          </div>

          {(baselineResults.gmm || baselineResults.shemI) && (
            <div className="mt-3 border-t border-dashed border-gray-200 dark:border-slate-700 pt-2">
              <div className="flex items-center justify-between text-xs">
                <span className="font-medium text-gray-700 dark:text-slate-200">
                  Baselines available
                </span>
                <span className="text-[11px] text-gray-500 dark:text-slate-400">
                  Select a baseline to compare
                </span>
              </div>

              <div className="mt-2 flex flex-wrap gap-2">
                {baselineResults.gmm && (
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedBaseline("gmm")
                      persistBaselines(baselineResults, "gmm")
                    }}
                    className={[
                      "px-2.5 py-1 rounded-full text-[11px] border",
                      "transition-colors",
                      selectedBaseline === "gmm"
                        ? "bg-blue-600 text-white border-blue-600"
                        : "bg-gray-100 text-gray-800 border-gray-300 dark:bg-slate-800 dark:text-slate-100 dark:border-slate-600",
                    ].join(" ")}
                  >
                    GMM baseline (F1 nodes{" "}
                    {baselineResults.gmm.metrics.nodes.f1Score})
                  </button>
                )}

                {baselineResults.shemI && (
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedBaseline("shemI")
                      persistBaselines(baselineResults, "shemI")
                    }}
                    className={[
                      "px-2.5 py-1 rounded-full text-[11px] border",
                      "transition-colors",
                      selectedBaseline === "shemI"
                        ? "bg-blue-600 text-white border-blue-600"
                        : "bg-gray-100 text-gray-800 border-gray-300 dark:bg-slate-800 dark:text-slate-100 dark:border-slate-600",
                    ].join(" ")}
                  >
                    ShemI baseline (F1 nodes{" "}
                    {baselineResults.shemI.metrics.nodes.f1Score})
                  </button>
                )}
              </div>

              {selectedBaseline && selectedBaselineRun && baselineMetrics && (
                <div className="mt-3 text-[11px] text-gray-700 dark:text-slate-300 space-y-1.5">
                  <p className="font-semibold">
                    Comparing with {baselineLabel(selectedBaseline)} baseline
                  </p>

                  {/* Nodes comparison */}
                  <div className="border rounded-md p-2 bg-white/80 dark:bg-slate-900/80 border-gray-200 dark:border-slate-700">
                    <div className="text-[10px] font-semibold mb-1 text-gray-600 dark:text-slate-400">
                      Nodes
                    </div>
                    <div className="grid grid-cols-4 gap-1 text-[10px]">
                      <div />
                      <div className="text-right font-medium">PG-HIVE</div>
                      <div className="text-right font-medium">
                        {baselineLabel(selectedBaseline)}
                      </div>
                      <div className="text-right font-medium">
                        Δ (ours - baseline)
                      </div>

                      <div>F1</div>
                      <div className="text-right font-mono">
                        {metrics.nodes.f1Score}
                      </div>
                      <div className="text-right font-mono">
                        {baselineMetrics.nodes.f1Score}
                      </div>
                      {(() => {
                        const delta =
                          metrics.nodes.f1Score -
                          baselineMetrics.nodes.f1Score
                        return (
                          <div className={deltaClasses(delta)}>
                            {delta.toFixed(3)}
                          </div>
                        )
                      })()}

                      <div>Precision</div>
                      <div className="text-right font-mono">
                        {metrics.nodes.precision}
                      </div>
                      <div className="text-right font-mono">
                        {baselineMetrics.nodes.precision}
                      </div>
                      {(() => {
                        const delta =
                          metrics.nodes.precision -
                          baselineMetrics.nodes.precision
                        return (
                          <div className={deltaClasses(delta)}>
                            {delta.toFixed(3)}
                          </div>
                        )
                      })()}

                      <div>Recall</div>
                      <div className="text-right font-mono">
                        {metrics.nodes.recall}
                      </div>
                      <div className="text-right font-mono">
                        {baselineMetrics.nodes.recall}
                      </div>
                      {(() => {
                        const delta =
                          metrics.nodes.recall -
                          baselineMetrics.nodes.recall
                        return (
                          <div className={deltaClasses(delta)}>
                            {delta.toFixed(3)}
                          </div>
                        )
                      })()}
                    </div>
                  </div>

                  {/* Edges comparison */}
                  {selectedBaseline === "gmm" ? (
                    <div className="border rounded-md p-2 bg-white/60 dark:bg-slate-900/60 border-dashed border-gray-200 dark:border-slate-700">
                      <div className="text-[10px] font-semibold mb-1 text-gray-600 dark:text-slate-400">
                        Edges
                      </div>
                      <p className="text-[10px] text-gray-600 dark:text-slate-400">
                        GMM baseline is node-only. Edge-level comparison is not
                        available.
                      </p>
                    </div>
                  ) : (
                    baselineMetrics.edges &&
                    metrics.edges && (
                      <div className="border rounded-md p-2 bg-white/80 dark:bg-slate-900/80 border-gray-200 dark:border-slate-700">
                        <div className="text-[10px] font-semibold mb-1 text-gray-600 dark:text-slate-400">
                          Edges
                        </div>
                        <div className="grid grid-cols-4 gap-1 text-[10px]">
                          <div />
                          <div className="text-right font-medium">
                            PG-HIVE
                          </div>
                          <div className="text-right font-medium">
                            {baselineLabel(selectedBaseline)}
                          </div>
                          <div className="text-right font-medium">
                            Δ (ours - baseline)
                          </div>

                          <div>F1</div>
                          <div className="text-right font-mono">
                            {metrics.edges.f1Score}
                          </div>
                          <div className="text-right font-mono">
                            {baselineMetrics.edges.f1Score}
                          </div>
                          {(() => {
                            const delta =
                              metrics.edges.f1Score -
                              baselineMetrics.edges.f1Score
                            return (
                              <div className={deltaClasses(delta)}>
                                {delta.toFixed(3)}
                              </div>
                            )
                          })()}

                          <div>Precision</div>
                          <div className="text-right font-mono">
                            {metrics.edges.precision}
                          </div>
                          <div className="text-right font-mono">
                            {baselineMetrics.edges.precision}
                          </div>
                          {(() => {
                            const delta =
                              metrics.edges.precision -
                              baselineMetrics.edges.precision
                            return (
                              <div className={deltaClasses(delta)}>
                                {delta.toFixed(3)}
                              </div>
                            )
                          })()}

                          <div>Recall</div>
                          <div className="text-right font-mono">
                            {metrics.edges.recall}
                          </div>
                          <div className="text-right font-mono">
                            {baselineMetrics.edges.recall}
                          </div>
                          {(() => {
                            const delta =
                              metrics.edges.recall -
                              baselineMetrics.edges.recall
                            return (
                              <div className={deltaClasses(delta)}>
                                {delta.toFixed(3)}
                              </div>
                            )
                          })()}
                        </div>
                      </div>
                    )
                  )}
                </div>
              )}

              {selectedBaseline && !selectedBaselineRun && (
                <div className="mt-2 text-[11px] text-red-600 dark:text-red-400">
                  No baseline data found for {baselineLabel(selectedBaseline)} on{" "}
                  {datasetId}.
                </div>
              )}
            </div>
          )}
        </div>

        {/* Evaluation Results - Show button when results are available */}
        {evaluationResults && (
          <div className="rounded-lg p-3 shadow-sm border bg-white/90 border-gray-200 dark:bg-slate-800/90 dark:border-slate-700 mt-3">
            <button
              type="button"
              onClick={() => setShowEvaluationModal(true)}
              className="w-full px-4 py-3 rounded-md bg-gradient-to-r from-emerald-500 to-blue-600 text-white font-semibold hover:from-emerald-600 hover:to-blue-700 transition-all shadow-md hover:shadow-lg"
            >
              Show Extensive Evaluation
            </button>
          </div>
        )}

        {/* Run config wizard */}
        <RunConfigWizard
          open={runModalOpen}
          onOpenChange={setRunModalOpen}
          initialRunParams={runParams}
          onSubmit={async ({ conn, runParams: newParams }) => {
            if (setRunParams) {
              setRunParams(newParams)
              if (onDatasetChange) {
                onDatasetChange(newParams.dataset)
              }
            }

            // Όταν ξεκινάς νέο run, καθάρισε baselines για το νέο dataset
            setBaselineResults({ gmm: null, shemI: null })
            setSelectedBaseline(null)
            if (typeof window !== "undefined") {
              try {
                const key = `pg_baselines_${newParams.dataset}`
                window.sessionStorage.removeItem(key)
              } catch {}
            }

            try {
              sessionStorage.setItem("pg_demo_mode", "true")
              sessionStorage.setItem(
                "pg_demo_conn",
                JSON.stringify({ ...conn, ts: Date.now() })
              )
              sessionStorage.setItem(
                "pg_last_run_params",
                JSON.stringify(newParams)
              )
            } catch {}

            if (onRunClick) {
              await onRunClick(newParams)
            }

            await new Promise((resolve) => setTimeout(resolve, 600))
          }}
        />

        {/* Evaluation modal */}
        <Dialog
          open={showEvaluationModal}
          onOpenChange={setShowEvaluationModal}
        >
          <DialogContent className="min-w-[90vw] max-w-[90vw] max-h-[90vh] overflow-y-auto overflow-x-hidden">
            <DialogHeader>
              <DialogTitle>Extensive Evaluation</DialogTitle>
            </DialogHeader>

            <div className="mt-4">
              <EvaluationCharts dataset={datasetId} />
            </div>

            <div className="flex justify-end gap-2 mt-4">
              <button
                type="button"
                className="px-4 py-2 rounded-md bg-gray-200 text-gray-800 hover:bg-gray-300 dark:bg-slate-700 dark:text-slate-100 dark:hover:bg-slate-600"
                onClick={() => setShowEvaluationModal(false)}
              >
                Close
              </button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Baseline modal */}
        <Dialog
          open={baselineModalOpen}
          onOpenChange={(open) => {
            if (!isBaselineRunning) setBaselineModalOpen(open)
          }}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Run baselines</DialogTitle>
            </DialogHeader>

            <p className="text-sm text-gray-700 dark:text-slate-300 mb-3">
              Choose which baseline you want to run for the current dataset.
            </p>

            <div className="grid grid-cols-2 gap-3 mb-3">
              <button
                type="button"
                onClick={() => setBaselineChoice("gmm")}
                className={[
                  "border rounded-md p-3 text-left text-sm transition-colors",
                  baselineChoice === "gmm"
                    ? "border-blue-600 bg-blue-50 dark:bg-slate-800 dark:border-blue-400"
                    : "border-gray-200 bg-gray-50 hover:bg-gray-100 dark:border-slate-700 dark:bg-slate-900 dark:hover:bg-slate-800",
                ].join(" ")}
              >
                <div className="font-semibold mb-1">GMMSchema</div>
                <div className="text-xs text-gray-600 dark:text-slate-400">
                  Gaussian Mixture Model baseline clustering (nodes only).
                </div>
              </button>

              <button
                type="button"
                onClick={() => setBaselineChoice("shemI")}
                className={[
                  "border rounded-md p-3 text-left text-sm transition-colors",
                  baselineChoice === "shemI"
                    ? "border-blue-600 bg-blue-50 dark:bg-slate-800 dark:border-blue-400"
                    : "border-gray-200 bg-gray-50 hover:bg-gray-100 dark:border-slate-700 dark:bg-slate-900 dark:hover:bg-slate-800",
                ].join(" ")}
              >
                <div className="font-semibold mb-1">ShemI</div>
                <div className="text-xs text-gray-600 dark:text-slate-400">
                  Alternative label-based baseline (nodes & edges).
                </div>
              </button>
            </div>

            {isBaselineRunning && (
              <div className="flex items-center gap-2 text-xs text-blue-700 dark:text-blue-300 mb-3">
                <span className="inline-block h-3 w-3 rounded-full border-2 border-blue-500 border-t-transparent animate-spin" />
                <span>Running {baselineLabel(baselineChoice)} baseline...</span>
              </div>
            )}

            <div className="flex justify-end gap-2 mt-2">
              <button
                type="button"
                className="px-3 py-1.5 rounded-md border text-xs bg-white text-gray-700 hover:bg-gray-50 dark:bg-slate-800 dark:text-slate-100 dark:border-slate-600 dark:hover:bg-slate-700"
                onClick={() =>
                  !isBaselineRunning && setBaselineModalOpen(false)
                }
                disabled={isBaselineRunning}
              >
                Cancel
              </button>
              <button
                type="button"
                className="px-3 py-1.5 rounded-md text-xs font-medium bg-blue-600 text-white hover:bg-blue-700 disabled:bg-blue-400"
                onClick={handleRunBaseline}
                disabled={isBaselineRunning}
              >
                {isBaselineRunning
                  ? "Running..."
                  : `Run ${baselineLabel(baselineChoice)}`}
              </button>
            </div>
          </DialogContent>
        </Dialog>


        {/* Bottom buttons */}
        <div className="flex gap-2 mt-3 flex-wrap">
          <button
            type="button"
            onClick={() => setRunModalOpen(true)}
            className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 disabled:from-blue-400 disabled:to-indigo-400 text-white font-medium py-2 px-6 rounded-md flex-1 flex items-center justify-center shadow-md transition-all"
          >
            Run a new dataset
          </button>
          {/* if iterations do not show the button */}
          {activeRun?.params?.incremental ? null : (
          <button
            type="button"
            onClick={() => setBaselineModalOpen(true)}
            className="px-3 py-2 rounded-md border bg-white text-gray-800 hover:bg-gray-50 border-gray-300 dark:bg-slate-800 dark:text-slate-100 dark:border-slate-600 dark:hover:bg-slate-700 flex items-center justify-center text-sm transition-colors"
          >
            Run baselines
          </button>
          )}

          {/* Run Extensive Evaluation button - only when checkbox is NOT checked */}
          {!runParams?.extensiveEvaluation && (
            <button
              type="button"
              onClick={handleRunEvaluation}
              disabled={isEvaluationRunning}
              className="px-3 py-2 rounded-md border bg-white text-gray-800 hover:bg-gray-50 border-gray-300 dark:bg-slate-800 dark:text-slate-100 dark:border-slate-600 dark:hover:bg-slate-700 flex items-center justify-center text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isEvaluationRunning ? "Running..." : "Run Extensive Evaluation"}
            </button>
          )}

          <button
            type="button"
            onClick={onShowLogs}
            title="Show latest run logs for this dataset"
            className="px-3 py-2 rounded-md border bg-white text-gray-800 hover:bg-gray-50 border-gray-300 dark:bg-slate-800 dark:text-slate-100 dark:border-slate-600 dark:hover:bg-slate-700 flex items-center gap-2 text-sm transition-colors"
          >
            <FileText className="h-4 w-4" />
            Logs
          </button>

          {/* Iterations button – μόνο αν το activeRun είναι incremental και έχει iterations */}
          {!evaluationResults && activeRun?.params?.incremental &&
            Array.isArray(activeRun.iterations) &&
            activeRun.iterations.length > 0 && (
              <button
                type="button"
                onClick={onShowIterations}
                className="px-3 py-2 rounded-md border bg-white text-gray-800 hover:bg-gray-50 border-gray-300 dark:bg-slate-800 dark:text-slate-100 dark:border-slate-600 dark:hover:bg-slate-700 flex items-center justify-center text-sm transition-colors"
              >
                Iterations
              </button>
            )}
        </div>
      </div>
    </div>
  )
}
